/**
 * AI Provider 베이스 클래스
 */

import type { AIProvider, AIResponseWithReason, ContinueContext } from '../../types'
import { PROMPTS, getContinuePrompt } from '../../writing-utils/writing-prompts'
import { parseAIResponse, parseContinueResponse, parseTextResponse } from '../../writing-utils/writing-response-parser'
import { systemPromptStore } from '$lib/stores/systemPrompt'

export abstract class BaseProvider implements AIProvider {
  abstract name: string
  abstract type: 'direct' | 'proxy'
  
  /**
   * 실제 AI API 호출을 수행하는 추상 메서드
   * 각 Provider가 구현해야 함
   */
  protected abstract callAPI(systemPrompt: string, userPrompt: string): Promise<string>
  
  /**
   * 공통 paraphrase 구현
   */
  async paraphrase(text: string, options?: any): Promise<AIResponseWithReason> {
    const prompts = PROMPTS.paraphrase
    const response = await this.callAPI(prompts.system, prompts.user(text))
    
    return parseAIResponse(response, {
      fields: {
        primary: ['improved', 'paraphrased'],
        reason: 'reason'
      },
      defaultReason: prompts.defaultReason
    })
  }
  
  /**
   * 공통 expand 구현
   */
  async expand(text: string, options?: any): Promise<AIResponseWithReason> {
    const prompts = PROMPTS.expand
    const response = await this.callAPI(prompts.system, prompts.user(text))
    
    return parseAIResponse(response, {
      fields: {
        primary: 'expanded',
        reason: 'reason'
      },
      defaultReason: prompts.defaultReason
    })
  }
  
  /**
   * 공통 shrink 구현
   */
  async shrink(text: string, options?: any): Promise<AIResponseWithReason> {
    const prompts = PROMPTS.shrink
    const response = await this.callAPI(prompts.system, prompts.user(text))
    
    return parseAIResponse(response, {
      fields: {
        primary: 'shortened',
        reason: 'reason'
      },
      defaultReason: prompts.defaultReason
    })
  }
  
  /**
   * 공통 summarize 구현
   */
  async summarize(text: string, options?: any): Promise<string> {
    const prompts = PROMPTS.summarize
    const response = await this.callAPI(prompts.system, prompts.user(text))
    return parseTextResponse(response)
  }
  
  /**
   * 공통 continue 구현
   */
  async continue(text: string, context?: ContinueContext): Promise<string | AIResponseWithReason> {
    const mode = context?.mode || 'sentence'
    const prompts = getContinuePrompt(mode, context)
    const response = await this.callAPI(prompts.system, prompts.user(text))
    
    const result = parseContinueResponse(response, prompts.defaultReason)
    
    // Continue is always generation, not modification - don't show reason
    if (typeof result === 'object' && result.text) {
      return {
        text: result.text,
        reason: undefined
      }
    }
    
    return result
  }
  
  /**
   * 공통 critique 구현
   */
  async critique(text: string): Promise<string> {
    const prompts = PROMPTS.critique
    const response = await this.callAPI(prompts.system, prompts.user(text))
    return parseTextResponse(response)
  }
  
  /**
   * Custom prompt processing
   */
  async processCustomPrompt(text: string, prompt: string, mode?: 'sentence' | 'paragraph'): Promise<AIResponseWithReason> {
    // Check if this is a generation request (empty text) or modification request
    const isGeneration = !text || text.trim() === ''
    
    let systemPrompt: string
    let userPrompt: string
    
    if (isGeneration) {
      // Text generation mode
      let baseSystemPrompt = `당신은 한국어 텍스트를 생성하는 전문 작가입니다.
사용자의 요청에 따라 적절한 텍스트를 생성하고, 생성된 텍스트만 반환하세요.`
      
      // Add user's system prompt if available
      const userSystemPrompt = systemPromptStore.getPrompt()
      if (userSystemPrompt) {
        baseSystemPrompt = `사용자 지정 스타일: ${userSystemPrompt}\n\n${baseSystemPrompt}`
      }
      systemPrompt = baseSystemPrompt
      
      userPrompt = `다음 요청에 따라 새로운 텍스트를 생성해주세요:\n\n요청사항: ${prompt}\n\n`
      
      if (mode === 'sentence') {
        userPrompt += `한 문장으로 간결하게 작성하세요.\n`
      } else if (mode === 'paragraph') {
        userPrompt += `3-5 문장으로 구성된 한 문단으로 작성하세요.\n`
      }
      
      userPrompt += `생성된 텍스트만 반환하세요. 추가 설명이나 문맥 정보는 포함하지 마세요.`
    } else {
      // Text modification mode
      let baseSystemPrompt = `당신은 한국어 텍스트를 사용자의 요청에 따라 수정하는 전문 편집자입니다.
사용자가 요청한 대로 정확히 수정하고, 수정된 텍스트만 반환하세요.`
      
      // Add user's system prompt if available
      const userSystemPrompt = systemPromptStore.getPrompt()
      if (userSystemPrompt) {
        baseSystemPrompt = `사용자 지정 스타일: ${userSystemPrompt}\n\n${baseSystemPrompt}`
      }
      systemPrompt = baseSystemPrompt
      
      userPrompt = `다음 텍스트를 "${prompt}" 요청에 따라 수정해주세요:\n\n원문: ${text}\n\n요청사항: ${prompt}\n\n`
      
      if (mode === 'sentence') {
        userPrompt += `한 문장으로 간결하게 작성하세요.\n`
      } else if (mode === 'paragraph') {
        userPrompt += `한 문단으로 자세하게 작성하세요.\n`
      }
      
      userPrompt += `수정된 텍스트만 반환하세요. 추가 설명이나 문맥 정보는 포함하지 마세요.`
    }
    
    const response = await this.callAPI(systemPrompt, userPrompt)
    
    // Only provide reason for modifications, not for generations
    if (isGeneration) {
      return {
        text: response,
        reason: undefined
      }
    }
    
    return {
      text: response,
      reason: `프롬프트: "${prompt}"`
    }
  }
  
  /**
   * Custom prompt processing with streaming (fallback to non-streaming)
   */
  async processCustomPromptStreaming(
    text: string, 
    prompt: string, 
    mode?: 'sentence' | 'paragraph',
    onChunk?: (chunk: string) => void
  ): Promise<AIResponseWithReason> {
    // Default implementation: fall back to non-streaming version
    const result = await this.processCustomPrompt(text, prompt, mode)
    
    // Call onChunk with the entire result at once
    if (onChunk && result.text) {
      onChunk(result.text)
    }
    
    return result
  }
}